import {
  reactive,
  onBeforeMount,
  computed,
  getCurrentInstance,
  watch,
} from "@vue/composition-api";
import store from "@/store";
import router from "@/router";

import useDynamicFields from "@/views/components/dynamic-fields/hooks/useDynamicFields";
import useValidationModal from "@/@core/components/base-modal/hooks/useValidationModal";
import { setBannerModel, bannerModel } from "@/models/banner/banner";
import { dynamicTableModel } from "@/models/data-management/dynamic-table";

import ImageInput from "@/assets/images/icons/image-input.svg";

export default function useBannerCreateEditForm(props) {
  const vm = getCurrentInstance().proxy;

  const { fetchSetBannerListModel, assignToSetBannersModel } = setBannerModel();
  const {
    fetchBannerFieldModel,
    createBannerModel,
    fetchBannerDetailModel,
  } = bannerModel();

  const { editDataModel } = dynamicTableModel();

  const { openDefaultErrorModal, openSuccessModal } = useValidationModal();
  const { resolveFields } = useDynamicFields();

  const loading = reactive({
    bannerInfo: false,
    submit: false,
  });

  const bannerForm = reactive({
    information: { payload: [] },
    image: { file: null, url: null },
    assignedSetBanner: [],
    bannerId: null,
  });

  const tagOption = reactive({
    tag: [],
    assignSetBanner: [],
  });

  const fetchSetBannerList = async () => {
    loading.bannerInfo = true;
    fetchSetBannerListModel.payload.Limit = undefined;

    try {
      const response = await store.dispatch(
        "api/apiRequest",
        fetchSetBannerListModel
      );

      if (response) {
        tagOption.assignSetBanner = response.data.payload;
        loading.bannerInfo = false;
      }
    } catch (err) {
      openDefaultErrorModal(err);
    }
  };

  const fetchDisplayInformation = async () => {
    window.$loading(true);
    loading.bannerInfo = true;
    try {
      const response = await store.dispatch(
        "api/apiRequest",
        fetchBannerFieldModel
      );

      if (response) {
        bannerForm.information.payload = response.data.dynamic_fields;

        if (props.isEdit || props.bannerId) {
          return fetchBannerDetail();
        }

        fetchSetBannerList();
      }
    } catch (err) {
      openDefaultErrorModal(err);
    } finally {
      window.$loading(false);
      loading.bannerInfo = false;
    }
  };

  const fetchBannerDetail = async () => {
    window.$loading(true);

    fetchBannerDetailModel.payload.Banner_Id = props.bannerId;

    try {
      const response = await store.dispatch(
        "api/apiRequest",
        fetchBannerDetailModel
      );

      if (response) {
        await resolveBannerDetail(response.data);
        fetchSetBannerList();
      }
    } catch (err) {
      openDefaultErrorModal(err);
    } finally {
      window.$loading(false);
      loading.bannerInfo = false;
    }
  };


  const createBanner = () => {
    window.$loading(true);
    loading.submit = true;

    store
      .dispatch("api/apiFormRequest", createBannerModel)
      .then((response) => {
        const bannerId = response.data.created_id;
        router
          .replace(`${router.currentRoute.path}?bannerId=${bannerId}`)
          .catch(() => {});
        vm.$emit("onGetBannerId", bannerId);

        assignToSetBanner(bannerId);
      })
      .catch((err) => {
        openDefaultErrorModal(err);
      })
      .finally(() => {
        window.$loading(false);
        loading.submit = false;
      });
  };

  const assignToSetBanner = (bannerId) => {
    window.$loading(true);
    const assignedSetBannerList = [];

    bannerForm.assignedSetBanner.map((setBanner, index) =>
      assignedSetBannerList.push({
        Seq: index + 1,
        Set_Banner_Id: setBanner.set_banner_id,
      })
    );

    assignToSetBannersModel.payload.Banner_Id = bannerId;
    assignToSetBannersModel.payload.Banner_Assign_To_Set_Banners = assignedSetBannerList;

    store
      .dispatch("api/apiRequest", assignToSetBannersModel)
      .then((response) => {
        vm.$emit("onNextStep");
      })
      .catch((err) => {
        openDefaultErrorModal(err);
      })
      .finally(() => {
        window.$loading(false);
      });
  };

  const editBanner = (bannerId, bannerGroupId) => {
    window.$loading(true);
    loading.submit = true;

    store
      .dispatch("api/apiFormRequest", editDataModel)
      .then((response) => {
        if (bannerForm.assignedSetBanner.length > 0) {
          return assignToSetBanner(bannerId);
        }
        vm.$emit("onNextStep");
      })
      .catch((err) => {
        openDefaultErrorModal(err);
      })
      .finally(() => {
        window.$loading(false);
        loading.submit = false;
      });
  };

  const resolveBannerDetail = (bannerDetail) =>
    new Promise((resolve, reject) => {
      try {
        bannerForm.information.payload.map((field) => {
          field.value = bannerDetail.payload[field.key.toLowerCase()];
        });

        bannerForm.image.url = bannerDetail.payload.banner_image_image_url;
        bannerForm.assignedSetBanner = bannerDetail.mapping_set_banner;
        resolve();
      } catch (err) {
        reject(err);
      }
    });

  const resolveAssignedBanner = () => {
    const assignBannerList = [];

    bannerForm.assignedSetBanner.map((setBanner) => {
      const assignedSetBanner = tagOption.assignSetBanner.find(
        (setBannerOption) =>
          setBannerOption.set_banner_id == setBanner.set_banner_id
      );

      if (assignedSetBanner) {
        assignBannerList.push(assignedSetBanner);
      }
    });

    bannerForm.assignedSetBanner = assignBannerList;
  };

  const renderImageUrl = computed(() => {
    const img = bannerForm.imageUrl || ImageInput;

    return {
      "background-image": `url(${img})`,
    };
  });

  const buildBannerInformationPayload = () =>
    new Promise((resolve, reject) => {
      const payload = {};

      bannerForm.information.payload.map((field) => {
        if (field.type_id == 11) {
          return (payload[field.api_key] = resolveDateObject(
            vm.$refs[field.key][0].fieldValue
          ));
        }

        return (payload[field.api_key] = vm.$refs[field.key][0].fieldValue);
      });

      resolve(payload);
    });

  const resolveDateObject = (dateObject) => {
    const date = dateObject.date.split("-");
    const time = dateObject.time.split(":");

    const resolvedDate = `${date[2]}-${date[1]}-${date[0]} ${time[0]}:${time[1]}:00`;

    return resolvedDate;
  };

  const onUpload = (image) => {
    bannerForm.image.file = image.file;
    bannerForm.image.url = image.url;
  };

  const onSubmit = async () => {
    const payload = await buildBannerInformationPayload();
    const { params } = router.currentRoute;

    payload.TableName = "Banner_Banner";
    payload.Banner_Image_Image_Url = bannerForm.image.file;

    if (props?.isEdit && params.bannerId) {
      payload.BannerId = params.bannerId;
      editDataModel.payload = payload;

      return editBanner(params.bannerId);
    }
    payload.Seq = 1;
    createBannerModel.payload = payload;
    createBanner();
  };

  const resolveRedirect = () => {
    const { params } = router.currentRoute;

    if (params.ref == "Banner_Group") {
      if (params.setBannerId && params.bannerGroupId) {
        return router.push(
          `/banner/group/Banner_Group/${params.bannerGroupId}/info/set/${params.setBannerId}/info`
        );
      }

      if (params.bannerGroupId) {
        return router.push(
          `/banner/group/Banner_Group/${params.bannerGroupId}/info`
        );
      }

      if (params.setBannerId) {
        return router.push(
          `/banner/group/Banner_Group/set/${params.setBannerId}/info`
        );
      }

      return navigateToBannerGroupListPage();
    }

    if (params.ref == "Banner_Set") {
      if (params.setBannerId) {
        return router.push(`/banner/set/Banner_Set/${params.setBannerId}/info`);
      }
    }

    return navigateToBannerListPage();
  };

  const navigateToBannerListPage = () => {
    router.push("/banner/list/Banner_List");
  };

  const navigateToBannerGroupListPage = () => {
    router.push("/banner/group/Banner_Group");
  };

  onBeforeMount(() => {
    fetchDisplayInformation();
  });

  watch(
    () => tagOption.assignSetBanner,
    () => {
      if (tagOption.assignSetBanner.length > 0) {
        resolveAssignedBanner();
      }
    },
    {
      deep: true,
    }
  );

  return {
    loading,
    bannerForm,
    renderImageUrl,
    tagOption,
    fetchDisplayInformation,
    onUpload,
    resolveFields,
    onSubmit,
    resolveRedirect,
  };
}
